import { LoadingState } from '@lcs/shared/api';
import { createFeatureSelector, on } from '@ngrx/store';
import { OrderingDirections, OrderingOption } from 'generated/graphql-types';
import { SystemOrAccountRegisterState } from 'src/app/common-components/register/models/register-state';
import {
   RegisterAdapter,
   createRegisterAdapter,
} from 'src/app/common-components/register/models/register-state.adapter';
import { accountIdChange } from 'src/app/store/select-account/select-account.actions';
import { PhoneNumbersFilterOption } from '../models/phone-number-filter-option';
import { PhoneNumberRegisterOptions } from '../models/phone-number-register-options';
import { PhoneNumberRegisterListItem } from '../models/phone-number-register-results.model';
import * as phoneNumberRegisterActions from './phone-number-register.actions';

export const phoneNumberRegisterFeatureKey = 'phoneNumberRegister';
export interface PhoneNumberRegisterState
   extends SystemOrAccountRegisterState<PhoneNumberRegisterListItem, PhoneNumberRegisterOptions, OrderingOption> {
   filters: PhoneNumbersFilterOption[];
   includeInactive: boolean;
}

const adapter: RegisterAdapter<PhoneNumberRegisterListItem, PhoneNumberRegisterOptions, OrderingOption> =
   createRegisterAdapter<PhoneNumberRegisterListItem, PhoneNumberRegisterOptions, OrderingOption>({
      selectId: (phoneNumberRegisterItem) => {
         return phoneNumberRegisterItem.phoneNumberId ?? 0;
      },
   });

const initialState: PhoneNumberRegisterState = {
   ...adapter.getInitialState(),
   filters: [],
   includeInactive: false,
   orderingOptions: [{ direction: OrderingDirections.Asc, field: 'phoneNumber' }],
   isOnSystemPage: false,
};

export const phoneNumberRegisterReducer = adapter.createReducer(
   phoneNumberRegisterActions.phoneNumberRegisterAdapterActions,
   initialState,
   on(phoneNumberRegisterActions.phoneNumberRegisterAdapterActions.pageEnter, (state) => {
      return {
         ...state,
         callState: LoadingState.Loading,
      };
   }),
   on(phoneNumberRegisterActions.phoneNumberRegisterAdapterActions.loadSuccess, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loaded,
         endCursor: action.endCursor,
         hasNextPage: action.hasNextPage,
         phoneNumbers: action.items,
         totalCount: action.totalCount,
      };
   }),
   on(phoneNumberRegisterActions.phoneNumberRegisterAdapterActions.quickFilterChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         quickFilters: {
            ...state.quickFilters,
            ...action.quickFilters,
         },
      };
   }),
   on(phoneNumberRegisterActions.phoneNumberRegisterInitialFilter, (state, action) => {
      return {
         ...state,
         filters: action.filters,
      };
   }),
   on(phoneNumberRegisterActions.phoneNumberRegisterAdapterActions.orderingOptionsChanged, (state, action) => {
      return {
         ...state,
         callState: LoadingState.Loading,
         orderingOptions: state.orderingOptions
            ? [
                 ...state.orderingOptions
                    .filter((option) => !action.orderingOptions.some((newOption) => newOption.field === option.field))
                    .filter((option) => option.direction !== OrderingDirections.NotSet),
                 ...action.orderingOptions,
              ]
            : action.orderingOptions,
      };
   }),
   on(phoneNumberRegisterActions.phoneNumberRegisterAdapterActions.clearRegisterFilters, (state) => {
      return {
         ...state,
         quickFilters: initialState.quickFilters,
         includeInactive: initialState.includeInactive,
         orderingOptions: initialState.orderingOptions,
      };
   }),
   on(accountIdChange, (state, action) => {
      return {
         ...state,
         quickFilters: initialState.quickFilters,
      };
   }),
   on(phoneNumberRegisterActions.loadSystemPhoneNumbersSuccess, (state, action) => {
      return {
         ...state,
         isOnSystemPage: true,
      };
   }),
   on(phoneNumberRegisterActions.loadAccountPhoneNumbersSuccess, (state, action) => {
      return {
         ...state,
         isOnSystemPage: false,
      };
   })
);
export const phoneNumberRegisterSelector =
   createFeatureSelector<PhoneNumberRegisterState>(phoneNumberRegisterFeatureKey);
export const selectors = adapter.getSelectors(phoneNumberRegisterSelector);
export const {
   selectAll,
   selectCallState,
   selectTotalCount,
   selectEndCursor,
   selectQuickFilters,
   selectOrderingOptions,
   selectIds,
} = adapter.getSelectors(phoneNumberRegisterSelector);
